import { useBack } from '../navigation/navigation'
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useLocation, useNavigate, matchPath } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { AiChatRouteProvider } from '../contexts/AiChatRouteContext'
import PageHeaderContext from '../contexts/PageHeaderContext'
import Icon from './Icon'
import UserMenu from './UserMenu'
import { Button } from './ui'
import AiChatWidget from './AiChatWidget'
import EmployeeActionBanners from './EmployeeActionBanners'
import { EmployeeNotificationsProvider } from '../contexts/EmployeeNotificationsContext'
import './Layout.css'

function isEditPagePath(pathname) {
  const pathNorm = String(pathname || '').split('?')[0].split('#')[0].replace(/\/+$/, '')
  const lastSegment = pathNorm.split('/').filter(Boolean).pop()
  return lastSegment === 'edit' || Boolean(lastSegment?.endsWith('-edit'))
}

function routeHidesTopHeader(pathname) {
  const pathNorm = String(pathname || '').split('?')[0].split('#')[0].replace(/\/+$/, '')
  return isEditPagePath(pathname) || pathNorm === '/company/budgets'
}

function getCurrentHostname() {
  if (typeof window === 'undefined') return ''
  return window.location?.hostname || ''
}

function isTestStandHost(hostname) {
  const normalized = String(hostname || '').trim().toLowerCase()
  return normalized === '2pb.ktdesignstudio.ru' || normalized === '89.104.65.251'
}

function TestStandBanner({ hostname }) {
  if (!isTestStandHost(hostname)) return null

  return (
    <div className="test-stand-banner test-stand-banner--high-contrast" role="status" aria-label="Тестовая система">
      <span className="test-stand-banner__label">Тестовая система</span>
      <span className="test-stand-banner__host">{hostname}</span>
    </div>
  )
}

function SidebarNavIcon({ name }) {
  const props = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
    'data-sidebar-icon': name,
  }

  switch (name) {
    case 'objects':
      return (
        <svg {...props}>
          <path d="M3 21h18" />
          <path d="M5 21V7l8-4v18" />
          <path d="M19 21V11l-6-4" />
        </svg>
      )
    case 'budgets':
      return (
        <svg {...props}>
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      )
    case 'departments':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      )
    case 'reports':
      return (
        <svg {...props}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      )
    case 'ai':
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      )
    case 'contracts':
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      )
    case 'stages':
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    case 'acts':
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="16" y2="17" />
        </svg>
      )
    case 'clients':
      return (
        <svg {...props}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      )
    case 'team':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      )
    default:
      return null
  }
}

function Layout({ children }) {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [pageHeader, setPageHeader] = useState(null)
  const pageHeaderValue = useMemo(() => ({ setPageHeader }), [])

  // Проверка прав доступа
  const hasPermission = (permission) => {
    if (!user?.permissions) return false
    return user.permissions[permission] === true
  }

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('2pb-sidebar-collapsed') === '1'
    } catch {
      return false
    }
  })
  const [aiPanelOpen, setAiPanelOpen] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('2pb-ai-panel-open') === '1'
    } catch {
      return false
    }
  })
  const [budgetMenuExpanded, setBudgetMenuExpanded] = useState(false)
  const wasOnBudgetRef = useRef(location.pathname.startsWith('/admin/budget'))

  // Закрывать сайдбар при переходе на другую страницу (мобильный drawer)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const onBudget = location.pathname.startsWith('/admin/budget')
    if (!onBudget) {
      setBudgetMenuExpanded(false)
    } else if (!wasOnBudgetRef.current) {
      setBudgetMenuExpanded(true)
    }
    wasOnBudgetRef.current = onBudget
  }, [location.pathname])

  useEffect(() => {
    document.body.classList.toggle('sidebar-scroll-lock', sidebarOpen)
    return () => {
      document.body.classList.remove('sidebar-scroll-lock')
    }
  }, [sidebarOpen])

  const isActive = (path) => location.pathname === path

  const back = useBack(pageHeader?.backLink || '/company/team')

  const profileTo = user?.employee_full_name
    ? `/company/team/${encodeURIComponent(user.employee_id || user.employee_full_name)}`
    : '/settings'

  const profileNavActive = user?.employee_full_name
    ? (() => {
        const m = matchPath({ path: '/company/team/:name', end: true }, location.pathname)
        if (!m?.params?.name) return false
        let fromPath = String(m.params.name).trim()
        try {
          fromPath = decodeURIComponent(fromPath.replace(/\+/g, ' '))
        } catch {
          /* уже декодировано или невалидный % */
        }
        const fromUser = String(user.employee_full_name).trim()
        return fromPath === fromUser || fromPath === String(user.employee_id)
      })()
    : location.pathname === '/settings'

  /** Не подсвечивать «Команда», если открыта карточка «Мой профиль» (тот же URL-сегмент). */
  const teamNavActive =
    isActive('/company/team') ||
    (location.pathname.startsWith('/company/team/') && !profileNavActive)
  const budgetNavActive =
    isActive('/company/budgets') ||
    location.pathname.startsWith('/company/budgets/')
  const stageReferenceNavActive =
    isActive('/company/stages') ||
    location.pathname.startsWith('/stage/')

  const showBudgetInMenu = ['admin', 'accountant', 'department_head', 'management'].includes(user?.role)
  const showAdminSection = ['admin', 'management', 'department_head'].includes(user?.role)
  const showSystemToolsSection = ['admin', 'management'].includes(user?.role) && (hasPermission('reference.view') || hasPermission('deleted_records.view') || hasPermission('custom_fields.view'))

  const headerTitle = pageHeader?.title === null
    ? null
    : (pageHeader?.title !== undefined
      ? pageHeader.title
      : getPageTitle(location.pathname, user))

  const aiFullPage = location.pathname === '/ai' || location.pathname === '/ai/'
  const routeHidesTopHeaderFlag = routeHidesTopHeader(location.pathname)
  const topHeaderHidden = Boolean(pageHeader?.hideTopHeader) || routeHidesTopHeaderFlag
  const hostname = getCurrentHostname()
  const showTestStandBanner = isTestStandHost(hostname)

  const setAiPanelOpenPersist = (open) => {
    setAiPanelOpen(open)
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('2pb-ai-panel-open', open ? '1' : '0')
      }
    } catch {
      /* ignore */
    }
  }

  return (
    <PageHeaderContext.Provider value={pageHeaderValue}>
    <AiChatRouteProvider>
    <div className={`layout${aiFullPage ? ' layout--ai-full' : ''}${sidebarCollapsed ? ' layout--sidebar-collapsed' : ''}${showTestStandBanner ? ' layout--test-stand' : ''}`}>
      {/* Overlay для мобильного меню: клик закрывает сайдбар */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'sidebar-overlay-visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />
      <aside id="app-sidebar" className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''} ${sidebarCollapsed ? 'sidebar--collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="logo" title="Проектная артель">
            <img src="/logo/logo.png" alt="Проектная артель 2ПБ" className="logo-image logo-image-desktop" onError={(e) => { e.target.src = '/logo/logo.jpg'; }} />
            <img src="/logo/logo-mobile.png" alt="Проектная артель 2ПБ" className="logo-image logo-image-mobile" />
          </div>
          <button
            type="button"
            className="sidebar-collapse-toggle"
            onClick={() => {
              setSidebarCollapsed((c) => {
                const next = !c
                try {
                  if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('2pb-sidebar-collapsed', next ? '1' : '0')
                  }
                } catch {
                  /* ignore */
                }
                return next
              })
            }}
            aria-label={sidebarCollapsed ? 'Развернуть боковое меню' : 'Свернуть боковое меню'}
            aria-expanded={!sidebarCollapsed}
          >
            <Icon name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'} size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <Link
            to={profileTo}
            className={`nav-item nav-item-profile ${profileNavActive ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Мой профиль' : undefined}
          >
            <span className="nav-icon"><Icon name="userCardId" size={20} /></span>
            <span className="nav-text">Мой профиль</span>
          </Link>

          <div className="nav-divider" />
          <div className="nav-section-label" title={sidebarCollapsed ? 'Раздел: Работа' : undefined}>Работа</div>
          <Link
            to="/company/objects"
            className={`nav-item ${isActive('/company/objects') || location.pathname.startsWith('/object/') ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Объекты' : undefined}
          >
            <span className="nav-icon"><SidebarNavIcon name="objects" /></span>
            <span className="nav-text">Объекты</span>
          </Link>
          <Link
            to="/company/budgets"
            className={`nav-item ${budgetNavActive ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Бюджеты' : undefined}
          >
            <span className="nav-icon"><SidebarNavIcon name="budgets" /></span>
            <span className="nav-text">Бюджеты</span>
          </Link>
          <Link
            to="/company/departments"
            className={`nav-item ${isActive('/company/departments') || location.pathname.startsWith('/company/departments/') ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Подразделения' : undefined}
          >
            <span className="nav-icon"><SidebarNavIcon name="departments" /></span>
            <span className="nav-text">Подразделения</span>
          </Link>
          {hasPermission('reports.view') && (
            <Link
              to="/company/reports"
              className={`nav-item ${isActive('/company/reports') || location.pathname.startsWith('/company/reports/') ? 'active' : ''}`}
              title={sidebarCollapsed ? 'Отчетность' : undefined}
            >
              <span className="nav-icon"><SidebarNavIcon name="reports" /></span>
              <span className="nav-text">Отчетность</span>
            </Link>
          )}
          <Link to="/ai" className={`nav-item ${isActive('/ai') ? 'active' : ''}`} title={sidebarCollapsed ? 'ИИ-ассистент' : undefined}>
            <span className="nav-icon"><SidebarNavIcon name="ai" /></span>
            <span className="nav-text">ИИ-ассистент</span>
          </Link>

          <div className="nav-divider" />
          <div className="nav-section-label" title={sidebarCollapsed ? 'Раздел: Справочники' : undefined}>Справочники</div>
          <Link
            to="/company/contracts"
            className={`nav-item ${isActive('/company/contracts') || location.pathname.startsWith('/contract/') ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Договоры' : undefined}
          >
            <span className="nav-icon"><SidebarNavIcon name="contracts" /></span>
            <span className="nav-text">Договоры</span>
          </Link>
          <Link
            to="/company/stages"
            className={`nav-item ${stageReferenceNavActive ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Бюджеты стадий' : undefined}
          >
            <span className="nav-icon"><SidebarNavIcon name="stages" /></span>
            <span className="nav-text">Бюджеты стадий</span>
          </Link>
          <Link
            to="/company/acts"
            className={`nav-item ${isActive('/company/acts') || location.pathname.startsWith('/company/acts/') || location.pathname.startsWith('/act/') ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Акты' : undefined}
          >
            <span className="nav-icon"><SidebarNavIcon name="acts" /></span>
            <span className="nav-text">Акты</span>
          </Link>
          <Link
            to="/company/clients"
            className={`nav-item ${isActive('/company/clients') || location.pathname.startsWith('/client/') ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Клиенты' : undefined}
          >
            <span className="nav-icon"><SidebarNavIcon name="clients" /></span>
            <span className="nav-text">Клиенты</span>
          </Link>
          <Link
            to="/company/team"
            className={`nav-item ${teamNavActive ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Команда' : undefined}
          >
            <span className="nav-icon"><SidebarNavIcon name="team" /></span>
            <span className="nav-text">Команда</span>
          </Link>

          {showBudgetInMenu && (
            <>
              <button
                type="button"
                className="nav-subsection-toggle"
                onClick={() => setBudgetMenuExpanded((v) => !v)}
                aria-expanded={budgetMenuExpanded}
                aria-controls="nav-budget-submenu"
                title={sidebarCollapsed ? 'Бюджет (подменю)' : undefined}
              >
                <span className="nav-subsection-toggle-text">Бюджет</span>
                <Icon
                  name={budgetMenuExpanded ? 'chevronDown' : 'chevronRight'}
                  size={14}
                  className="nav-subsection-toggle-chevron"
                />
              </button>
              {budgetMenuExpanded && (
                <div id="nav-budget-submenu" className="nav-nested-group">
                  <Link
                    to="/admin/budget/employees"
                    className={`nav-item nav-item-nested ${isActive('/admin/budget/employees') ? 'active' : ''}`}
                    title={sidebarCollapsed ? 'Базовые проценты сотрудников' : undefined}
                  >
                    <span className="nav-icon"><Icon name="users" size={20} /></span>
                    <span className="nav-text">Базовые проценты сотрудников</span>
                  </Link>
                  <Link
                    to="/admin/budget/custom-team-percentages"
                    className={`nav-item nav-item-nested ${isActive('/admin/budget/custom-team-percentages') ? 'active' : ''}`}
                    title={sidebarCollapsed ? 'Уникальные проценты сотрудников' : undefined}
                  >
                    <span className="nav-icon"><Icon name="file" size={20} /></span>
                    <span className="nav-text">Уникальные проценты сотрудников</span>
                  </Link>
                  <Link
                    to="/admin/budget/gip-rules"
                    className={`nav-item nav-item-nested ${isActive('/admin/budget/gip-rules') ? 'active' : ''}`}
                    title={sidebarCollapsed ? 'Правила ГИПов' : undefined}
                  >
                    <span className="nav-icon"><Icon name="file" size={20} /></span>
                    <span className="nav-text">Правила ГИПов</span>
                  </Link>
                  <Link
                    to="/admin/budget/budget-rules"
                    className={`nav-item nav-item-nested ${location.pathname.startsWith('/admin/budget/budget-rules') ? 'active' : ''}`}
                    title={sidebarCollapsed ? 'Типы распределения' : undefined}
                  >
                    <span className="nav-icon"><Icon name="settings" size={20} /></span>
                    <span className="nav-text">Типы распределения</span>
                  </Link>
                </div>
              )}
            </>
          )}

          {(showAdminSection || showSystemToolsSection) && (
            <>
              <div className="nav-divider" />
              <div className="nav-section-label" title={sidebarCollapsed ? 'Раздел: Система' : undefined}>Система</div>
              {showAdminSection && (
                <>
                  {(user?.role === 'department_head' || hasPermission('users.view')) && (
                    <Link
                      to="/admin/users"
                      className={`nav-item ${isActive('/admin/users') || location.pathname.startsWith('/admin/users/') ? 'active' : ''}`}
                      title={sidebarCollapsed ? 'Пользователи' : undefined}
                    >
                      <span className="nav-icon"><Icon name="users" size={20} /></span>
                      <span className="nav-text">Пользователи</span>
                    </Link>
                  )}
                  {user?.role === 'admin' && hasPermission('roles.view') && (
                    <Link
                      to="/admin/roles"
                      className={`nav-item ${isActive('/admin/roles') ? 'active' : ''}`}
                      title={sidebarCollapsed ? 'Роли' : undefined}
                    >
                      <span className="nav-icon"><Icon name="lock" size={20} /></span>
                      <span className="nav-text">Роли</span>
                    </Link>
                  )}
                  {(['admin', 'management'].includes(user?.role) || hasPermission('settings.system_params')) && (
                    <Link
                      to="/admin/system-params"
                      className={`nav-item ${isActive('/admin/system-params') ? 'active' : ''}`}
                      title={sidebarCollapsed ? 'Параметры системы' : undefined}
                    >
                      <span className="nav-icon"><Icon name="settings" size={20} /></span>
                      <span className="nav-text">Параметры системы</span>
                    </Link>
                  )}
                  {(['admin', 'management'].includes(user?.role) || hasPermission('settings.system_params')) && (
                    <Link
                      to="/admin/bot-response-rules"
                      className={`nav-item ${isActive('/admin/bot-response-rules') ? 'active' : ''}`}
                      title={sidebarCollapsed ? 'Правила ответов бота' : undefined}
                    >
                      <span className="nav-icon"><Icon name="chat" size={20} /></span>
                      <span className="nav-text">Правила ответов бота</span>
                    </Link>
                  )}
                </>
              )}
              {showSystemToolsSection && (
                <>
                  {user?.role === 'admin' && hasPermission('reference.view') && (
                    <Link
                      to="/admin/reference"
                      className={`nav-item ${isActive('/admin/reference') ? 'active' : ''}`}
                      title={sidebarCollapsed ? 'Справочники (админ)' : undefined}
                    >
                      <span className="nav-icon"><Icon name="book" size={20} /></span>
                      <span className="nav-text">Справочники</span>
                    </Link>
                  )}
                  {user?.role === 'admin' && hasPermission('custom_fields.view') && (
                    <Link
                      to="/admin/custom-fields"
                      className={`nav-item ${isActive('/admin/custom-fields') ? 'active' : ''}`}
                      title={sidebarCollapsed ? 'Доп. поля' : undefined}
                    >
                      <span className="nav-icon"><Icon name="file" size={20} /></span>
                      <span className="nav-text">Доп. поля</span>
                    </Link>
                  )}
                </>
              )}
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <UserMenu sidebarCollapsed={sidebarCollapsed} />
        </div>
      </aside>
      <div className={`layout-main${topHeaderHidden ? ' layout-main--top-header-hidden' : ''}`}>
        <TestStandBanner hostname={hostname} />
        {!routeHidesTopHeaderFlag && (
          <header className="top-header">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label={sidebarOpen ? 'Закрыть меню' : 'Открыть меню'}
              aria-expanded={sidebarOpen}
              aria-controls="app-sidebar"
            >
              <Icon name="menu" size={24} />
            </button>
            {pageHeader?.backLink != null && (
              <Button
                onClick={back}
                className="btn-back-enhanced"
                variant="ghost"
              >
                {pageHeader.backLabel || '← Назад'}
              </Button>
            )}
            {headerTitle ? <h2 className="top-header-title">{headerTitle}</h2> : null}
            <div className="top-header-actions">
              {pageHeader?.renderActions?.()}
            </div>
          </header>
        )}
        <main className="main-content">
          <EmployeeNotificationsProvider>
            <EmployeeActionBanners />
            {children}
          </EmployeeNotificationsProvider>
        </main>
      </div>
      <AiChatWidget aiPanelOpen={aiPanelOpen} onAiPanelOpenChange={setAiPanelOpenPersist} />
    </div>
    </AiChatRouteProvider>
    </PageHeaderContext.Provider>
  )

  function getPageTitle(pathname, user) {
    const pathNorm = pathname.replace(/\/$/, '') || '/'
    // Для главной страницы учитываем роль пользователя
    if (pathNorm === '/') {
      if (user?.role === 'admin' || user?.role === 'management') {
        return 'Дэшборд компании'
      }
      return 'Дэшборд'
    }
    
    if (pathNorm === '/admin/users/new') {
      return 'Создание пользователя'
    }
    if (/^\/admin\/users\/[^/]+\/edit$/.test(pathNorm)) {
      return 'Редактирование пользователя'
    }

    const titles = {
      '/admin/users': 'Пользователи',
      '/admin/roles': 'Роли',
      '/admin/system-params': 'Параметры системы',
      '/admin/bot-response-rules': 'Правила ответов бота',
      '/admin/reference': 'Справочники',
      '/admin/custom-fields': 'Дополнительные поля',
      '/admin/deleted-records': 'Удаленные записи',
      '/company/clients': 'Клиенты',
      '/company/objects': 'Объекты',
      '/company/budgets': 'Бюджеты',
      '/company/contracts': 'Договоры',
      '/company/stages': 'Бюджеты стадий',
      '/company/acts': 'Акты',
      '/company/reports': 'Отчетность',
      '/company/team': 'Команда',
      '/company/departments': 'Подразделения',
      '/admin/budget/employees': 'Базовые проценты сотрудников',
      '/admin/budget/custom-team-percentages': 'Уникальные проценты сотрудников',
      '/admin/budget/gip-rules': 'Правила ГИПов',
      '/admin/budget/budget-rules': 'Типы распределения',
      '/settings': 'Настройки',
      '/ai': 'ИИ-ассистент'
    }
    
    // Для карточек определяем динамически
    if (pathNorm.startsWith('/client/')) {
      return 'Карточка клиента'
    }
    if (pathNorm.startsWith('/object/')) {
      return 'Карточка объекта'
    }
    if (pathNorm.startsWith('/contract/')) {
      return 'Карточка договора'
    }
    if (pathNorm.startsWith('/company/team/')) {
      return 'Карточка сотрудника'
    }
    if (pathNorm === '/admin/budget/budget-rules/new') {
      return 'Новый тип распределения'
    }
    if (/^\/admin\/budget\/budget-rules\/.+\/edit$/.test(pathNorm)) {
      return 'Редактирование типа распределения'
    }
    if (pathNorm.startsWith('/company/departments/') && pathNorm !== '/company/departments') {
      return 'Подразделение'
    }
    if (pathNorm.startsWith('/company/reports/') && pathNorm !== '/company/reports') {
      return 'Отчет'
    }
    if (pathNorm.startsWith('/company/budgets/') && pathNorm !== '/company/budgets') {
      return 'Бюджет стадии'
    }
    
    return titles[pathNorm] || titles[pathNorm.split('?')[0]] || 'Страница'
  }
}

export default Layout
